/**
 * @file ProfilePage.js
 * @module src/js/pages/memberZone/ProfilePage
 * @description
 * **Page component to display and edit the profile of the logged-in user.**
 * - Fetches profile data from the `/api/profile/:id` endpoint.
 * - Allows updating profile information and uploading a new profile picture.
 * - Supports deleting the user profile with confirmation.
 * - Enables marking the profile as graduated with confirmation.
 * - Use a message system to display success or error notifications.
 * - Redirects to the login page if the user is not authenticated.
 *
 * @version
 * <p>
 *  2025-11-24: First version with documentation. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-11-25: Added graduation marking features. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Move the messages to the modal system. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Migrate to useDeleteProfile hook. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-05: Migrate to useCheckAuthAndFetch hook. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-05: Migrate to useMarkAsGrad hook. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 * </p>
 */
import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useUser } from "../../contexts/UserProvider";
import { useModal } from "../../contexts/ModalProvider";
import useDeleteProfile from "../../hooks/useDeleteProfile";
import useCheckAuthAndFetch from "../../hooks/useCheckAuthAndFetch";
import useMarkAsGrad from "../../hooks/useMarkAsGrad";
import useUpdateProfile from "../../hooks/useUpdateProfile";
import Advisor from "../../components/Advisor";
import ThemeToggledWrapper from "../../components/ThemeToggledWrapper";
import ProfileDataContent from "../../components/ProfileDataContent";
import { StyledImg, LoadingThreeDots } from "../../components/UtilityComponents";

export default function ProfilePage() {
    const { state, pathname } = useLocation();
    const { uid } = useUser();
    const { showModalMessage } = useModal();
    const checkAuthAndFetch = useCheckAuthAndFetch();
    const [showInputs, setShowInputs] = useState(false);
    const [profileData, setProfileData] = useState({});
    const [updatedImgFile, setUpdatedImgFile] = useState([null]);
    const [updatedProfileData, setUpdatedProfileData] = useState({});
    const [updating, setUpdating] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [loading, setLoading] = useState(true);
    const headerRef = useRef(null);
    const uidRef = useRef(uid); // Stores the initial uid value. Its value won't change after the first render.
    const deleteProfile = useDeleteProfile({ setDeleting });
    const markAsGrad = useMarkAsGrad({
        setUpdating,
        setProfileData,
        setUpdatedProfileData,
    });
    const updateProfile = useUpdateProfile({
        setUpdatedProfileData,
        setProfileData,
        setUpdating,
        setShowInputs,
    });

    const contentNotChanged = JSON.stringify(profileData) === JSON.stringify(updatedProfileData);
    const imageNotUploaded = updatedImgFile[0] == null;
    

    useEffect(() => {
        if (state?.checkIsAdvisor && profileData.group && profileData.group !== 'teacher') {
            showModalMessage({ type: "error", content: "You have to log in as Advisor to edit Advisor's profile." });
        }
    }, [state, profileData.group, showModalMessage]);

    useEffect(() => {
        async function checkAuthAndFetchData() {
            const data = await checkAuthAndFetch({ request: `/api/profile/${uidRef.current}` });
            setProfileData({ ...data } || {}); // Independent copy
            setUpdatedProfileData({ ...data } || {}); // Independent copy
            setLoading(false);
        }
        checkAuthAndFetchData();
    }, [checkAuthAndFetch]);

    return (
        profileData && profileData.group === 'teacher' ? <Advisor /> : (
            <ThemeToggledWrapper id="profile" lightStyle={2} darkStyle={4}>
                <div className="inner">
                    <header
                        className="major special"
                        ref={headerRef}
                        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                    >
                        <h2>{uid}'s Profile</h2>
                        <span style={{ textAlign: 'center', fontSize: '0.9em', marginTop: '25px' }}>
                            (Synchronized across&nbsp;
                                <Link to='/members'>Members</Link>,&nbsp;
                                <Link to='/member-zone/contact'>Contact</Link>, and&nbsp;
                                <Link to='/member-zone/profile'>Profile</Link> pages)
                        </span>
                    </header>
                    <div id="wrapper" className="divided">
                        <section className="spotlight style1 orient-right content-align-left image-position-center onscroll-image-fade-in" id="first">
                            <div className="content">
                                {loading ? <LoadingThreeDots /> : (
                                    <ProfileDataContent
                                        showInputs={showInputs}
                                        profileData={profileData}
                                        updatedProfileData={updatedProfileData}
                                        setUpdatedProfileData={setUpdatedProfileData}
                                        updatedImgFile={updatedImgFile}
                                        setUpdatedImgFile={setUpdatedImgFile}
                                    />
                                )}
                                <ul className="actions special">
                                    <li>
                                        {/* Save button */}
                                        {showInputs && (
                                            <button
                                                onClick={async () => {
                                                    await updateProfile({
                                                        request: `/api/profile/${uid}`,
                                                        updatedProfileData,
                                                        updatedImgFile: updatedImgFile[0] || null,
                                                        from: pathname,
                                                    });
                                                    setTimeout(() => headerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 100);
                                                }}
                                                style={{ marginTop: '15px' }}
                                                disabled={
                                                    (contentNotChanged && imageNotUploaded) ||
                                                    updatedProfileData.name === '' ||
                                                    updatedProfileData.phone === '' ||
                                                    updatedProfileData.address === '' ||
                                                    updatedProfileData.home_phone === '' ||
                                                    updating
                                                }
                                                // Check updatedImgFile[0] == null instead of comparing names
                                                // because the updated image name may be the same as the original one
                                            >
                                                Save
                                            </button>
                                        )}

                                        {/* Edit / Cancel button */}
                                        <button
                                            onClick={() => {
                                                setShowInputs(prev => !prev);
                                                setTimeout(() => headerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 100);
                                                setUpdatedProfileData(profileData ? { ...profileData } : {});
                                                setUpdatedImgFile([null]);
                                            }}
                                            style={{
                                                marginTop: '15px',
                                                marginLeft: showInputs ? '15px' : '0px'
                                            }}
                                            disabled={updating || deleting}
                                        >
                                            {showInputs ? 'Cancel' : 'Edit Your Profile'}
                                        </button>

                                        {/* Delete and Mark as Graduated buttons */}
                                        {!showInputs && profileData.group === 'student' && (
                                            <>
                                                {/* Mark as Graduated button */}
                                                <button
                                                    onClick={async () => {
                                                        await markAsGrad({
                                                            id: profileData.member_ID,
                                                            institute: profileData.institute,
                                                            level: profileData.level,
                                                        });
                                                        setTimeout(() => headerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 100);
                                                    }}
                                                    style={{
                                                        display: 'block',
                                                        marginTop: '15px',
                                                        color: 'lightgreen',
                                                        outline: '2px solid lightgreen',
                                                        outlineOffset: '-2px',
                                                    }}
                                                    disabled={updating || deleting}
                                                >
                                                    Mark as Graduated
                                                </button>

                                                {/* Delete profile button */}
                                                <button
                                                    onClick={() => deleteProfile({ id: profileData.member_ID })}
                                                    style={{
                                                        display: 'block',
                                                        marginTop: '15px',
                                                        color: 'lightcoral',
                                                        outline: '2px solid lightcoral',
                                                        outlineOffset: '-2px',
                                                    }}
                                                    disabled={updating || deleting}
                                                >
                                                    Delete My Account
                                                </button>
                                            </>
                                        )}
                                    </li>
                                </ul>
                            </div>

                            {/* Profile Image */}
                            <span className="image" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <StyledImg
                                    src={profileData.photo !== 'none' ? profileData.photo_path : null}
                                    style={{
                                        borderRadius: '5px',
                                        boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.2)'
                                    }}
                                />
                            </span>
                        </section>
                    </div>
                </div>
            </ThemeToggledWrapper>
        )
    );
}
